/**
 * Protocolo de sinalização do SFU. Ver docs/05-voice.md.
 *
 * Separado do gateway de propósito: a sinalização de media é bursty
 * (dezenas de frames durante um join), latency-sensitive, e não deve
 * competir com o fan-out de mensagens no mesmo socket nem no mesmo processo.
 */
import { z } from 'zod';

export const VOICE_PROTOCOL_VERSION = 1;

/** TTL do voice token. Só é usado no join, logo não precisa de mais. */
export const VOICE_TOKEN_TTL_SECONDS = 60;

/** Credenciais de TURN efémeras — ver docs/05, secção coturn. */
export const TURN_CREDENTIAL_TTL_SECONDS = 120;

// ─── Claims do voice token ───────────────────────────────────────────────────

export interface VoiceTokenClaims {
  /** user id */
  sub: string;
  channelId: string;
  guildId: string;
  /**
   * Bitfield já resolvido pela API. O SFU NÃO consulta o Postgres: valida a
   * assinatura e confia no conteúdo. Mantém o media server estúpido — é o
   * processo mais exposto da stack.
   */
  permissions: string;
  /** Nó de SFU para onde este token é válido. */
  node: string;
  displayName: string;
  /**
   * Moderação imposta, resolvida pela API e assinada aqui dentro.
   *
   * Vai no token porque a reentrada tem de a respeitar: sem isto, um
   * utilizador silenciado por um moderador bastava-lhe sair e voltar a
   * entrar para ficar livre — e o SFU, que não consulta nada, não tinha
   * como saber.
   */
  serverMute: boolean;
  serverDeaf: boolean;
}

// ─── Controlo API → SFU ──────────────────────────────────────────────────────

/**
 * A API precisa de impor moderação a uma sessão de media JÁ ligada: o token
 * só cobre o momento do join.
 *
 * O canal é um POST em `/control` do SFU, autenticado com um JWT do MESMO
 * segredo das voice tokens mas com `audience` diferente — um token de join
 * não serve para controlar ninguém, e vice-versa. Toda a ordem viaja DENTRO
 * das claims, não no corpo: assim não há forma de a assinatura e o pedido
 * divergirem.
 */
export const VOICE_CONTROL_AUDIENCE = 'tasca-sfu-control';

/** Chega e sobra para um POST na mesma máquina. */
export const VOICE_CONTROL_TTL_SECONDS = 15;

export const voiceControlSchema = z.object({
  action: z.enum(['mute', 'deafen', 'disconnect']),
  channelId: z.string().min(1).max(20),
  userId: z.string().min(1).max(20),
  /** Valor novo para `mute`/`deafen`. Ignorado em `disconnect`. */
  value: z.boolean().default(true),
});

export type VoiceControlRequest = z.infer<typeof voiceControlSchema>;

export interface VoiceControlClaims extends VoiceControlRequest {
  node: string;
}

/**
 * `applied: false` não é um erro: significa que o utilizador não tinha (ou
 * já não tem) sessão de media neste nó. O estado em Redis continua a ser a
 * verdade, e o token do próximo join aplica-o.
 */
export interface VoiceControlResult {
  applied: boolean;
  reason?: string;
}

// ─── Mensagens cliente → SFU ─────────────────────────────────────────────────

export const voiceClientMessageSchema = z.discriminatedUnion('op', [
  z.object({
    op: z.literal('join'),
    token: z.string().min(1).max(4096),
    /** Capacidades RTP do device do cliente, do mediasoup-client. */
    rtpCapabilities: z.unknown(),
  }),
  z.object({
    op: z.literal('createTransport'),
    direction: z.enum(['send', 'recv']),
  }),
  z.object({
    op: z.literal('connectTransport'),
    transportId: z.string().max(128),
    dtlsParameters: z.unknown(),
  }),
  z.object({
    op: z.literal('produce'),
    transportId: z.string().max(128),
    kind: z.enum(['audio', 'video']),
    rtpParameters: z.unknown(),
    /** 'mic' | 'cam' | 'screen' — distingue câmara de partilha de ecrã. */
    source: z.enum(['mic', 'cam', 'screen']).default('mic'),
  }),
  z.object({
    op: z.literal('closeProducer'),
    producerId: z.string().max(128),
  }),
  z.object({
    op: z.literal('pauseProducer'),
    producerId: z.string().max(128),
    paused: z.boolean(),
  }),
  z.object({
    op: z.literal('consume'),
    transportId: z.string().max(128),
    producerId: z.string().max(128),
    rtpCapabilities: z.unknown(),
  }),
  z.object({
    op: z.literal('resumeConsumer'),
    consumerId: z.string().max(128),
  }),
  /**
   * Parar de RECEBER um consumer.
   *
   * Pausar só no cliente pararia de tocar mas o servidor continuava a
   * enviar. Num canal de 20 pessoas, ensurdecer poupa assim 19 streams de
   * downstream em vez de nenhum — e é a mesma op que serve para silenciar
   * uma pessoa só.
   */
  z.object({
    op: z.literal('pauseConsumer'),
    consumerId: z.string().max(128),
  }),
  /**
   * Que camada de simulcast encaminhar para ESTE consumidor.
   *
   * A decisão é do cliente porque é ele que sabe a que tamanho está a
   * mostrar o vídeo. Um tile de 160px a receber 720p desperdiça banda de
   * todos para pixels que ninguém vê — ver docs/05.
   */
  z.object({
    op: z.literal('setConsumerLayer'),
    consumerId: z.string().max(128),
    spatialLayer: z.number().int().min(0).max(2),
    /** Camada temporal (frame rate). Omitir = a mais alta disponível. */
    temporalLayer: z.number().int().min(0).max(2).optional(),
  }),
  /**
   * Prioridade entre consumidores do mesmo transport, 1-255.
   *
   * Quando a banda não dá para tudo, o mediasoup corta primeiro o que tem
   * prioridade mais baixa. Um screenshare de código ilegível é pior que uma
   * câmara granulada, logo o ecrã vale mais que a câmara.
   */
  z.object({
    op: z.literal('setConsumerPriority'),
    consumerId: z.string().max(128),
    priority: z.number().int().min(1).max(255),
  }),
  z.object({ op: z.literal('restartIce'), transportId: z.string().max(128) }),
  z.object({ op: z.literal('getStats') }),
  z.object({ op: z.literal('leave') }),
  z.object({ op: z.literal('ping') }),
]);

export type VoiceClientMessage = z.infer<typeof voiceClientMessageSchema>;

// ─── Mensagens SFU → cliente ─────────────────────────────────────────────────

export type VoiceServerMessage =
  | { op: 'hello'; v: number; node: string }
  | {
      op: 'joined';
      routerRtpCapabilities: unknown;
      /** Quem já está no canal, com os seus produtores. */
      peers: VoicePeer[];
    }
  | {
      op: 'transportCreated';
      direction: 'send' | 'recv';
      id: string;
      iceParameters: unknown;
      iceCandidates: unknown;
      dtlsParameters: unknown;
    }
  | { op: 'transportConnected'; transportId: string }
  | { op: 'produced'; producerId: string; kind: 'audio' | 'video'; source: string }
  | {
      op: 'consumed';
      consumerId: string;
      producerId: string;
      kind: 'audio' | 'video';
      rtpParameters: unknown;
      peerId: string;
      source: string;
      /** Já vem em pausa (server mute, ou o próprio silenciado). */
      paused: boolean;
    }
  | {
      op: 'newProducer';
      producerId: string;
      peerId: string;
      kind: string;
      source: string;
      paused: boolean;
    }
  | { op: 'producerClosed'; producerId: string; peerId: string }
  | { op: 'producerPaused'; producerId: string; peerId: string; paused: boolean }
  | { op: 'peerJoined'; peer: VoicePeer }
  | { op: 'peerLeft'; peerId: string }
  | { op: 'iceRestarted'; transportId: string; iceParameters: unknown }
  /** Moderação aplicada a esta sessão, por ordem da API. */
  | { op: 'moderated'; serverMute: boolean; serverDeaf: boolean }
  /**
   * A camada que está a ser encaminhada mudou.
   *
   * Pode ser porque o cliente a pediu, ou porque o estimador de banda do
   * mediasoup a baixou sozinho. `spatialLayer: null` significa que o
   * consumidor ficou sem camada nenhuma — banda insuficiente até para a
   * mais baixa, e é isso que a UI tem de dizer em vez de mostrar um
   * rectângulo preto.
   */
  | {
      op: 'consumerLayersChanged';
      consumerId: string;
      spatialLayer: number | null;
      temporalLayer: number | null;
    }
  /** Qualidade do stream, 0-10, do RTCP. Para o aviso de degradação na UI. */
  | { op: 'consumerScore'; consumerId: string; score: number; producerScore: number }
  | { op: 'stats'; stats: VoiceStats }
  | { op: 'pong' }
  | { op: 'error'; code: string; message?: string };

export interface VoicePeer {
  id: string;
  displayName: string;
  producers: { id: string; kind: 'audio' | 'video'; source: string; paused: boolean }[];
}

export interface VoiceStats {
  rtt?: number;
  /** Cumulativos: o indicador de qualidade calcula o rácio por diferenças. */
  packetsLost?: number;
  packetsSent?: number;
  jitter?: number;
  bitrate?: number;
}

/**
 * Qualidade de ligação em quatro níveis, derivada do RTT e da perda.
 *
 * Os limiares vêm do que a fala tolera, não de um número redondo: a 3% de
 * perda o FEC do Opus já não tapa tudo e começa a ouvir-se, a 8% a fala
 * parte-se; 200ms de RTT ainda deixa interromper naturalmente, 400ms dá
 * aquele atropelo de conversa por rádio.
 */
export type VoiceQuality = 'good' | 'fair' | 'poor' | 'unknown';

export function rateVoiceQuality(rttMs: number | undefined, lossRatio: number): VoiceQuality {
  if (rttMs === undefined) return 'unknown';
  if (lossRatio > 0.08 || rttMs > 400) return 'poor';
  if (lossRatio > 0.03 || rttMs > 200) return 'fair';
  return 'good';
}

// ─── Códigos de fecho ────────────────────────────────────────────────────────

export const VoiceCloseCode = {
  NORMAL: 1000,
  INVALID_TOKEN: 4401,
  TOKEN_EXPIRED: 4402,
  WRONG_NODE: 4403,
  NOT_JOINED: 4404,
  CHANNEL_FULL: 4405,
  /** Um moderador desligou, ou moveu, esta sessão. */
  DISCONNECTED: 4406,
  /** Outra sessão do mesmo utilizador entrou e substituiu esta. */
  REPLACED: 4407,
  RATE_LIMITED: 4408,
  PROTOCOL_ERROR: 4400,
} as const;

// ─── Perfis de media ─────────────────────────────────────────────────────────

/**
 * Áudio: mono a 64kbps com DTX.
 *
 * DTX é a optimização de maior retorno de todas: em silêncio o cliente
 * deixa de transmitir. Num canal de 20 pessoas com 2 a falar, corta ~85%
 * do tráfego de upstream.
 */
export const AUDIO_PROFILE = {
  codec: 'opus',
  channels: 1,
  sampleRate: 48_000,
  bitrate: 64_000,
  dtx: true,
  fec: true,
  ptime: 20,
} as const;

/**
 * Simulcast: três camadas espaciais, enviadas todas pelo emissor.
 *
 * O SFU escolhe por consumidor qual encaminha. Sem isto, um participante
 * com rede fraca obriga o emissor a baixar a qualidade para TODOS — é o SFU
 * a ter de escolher uma única camada para o pior cliente.
 *
 * Custo para quem envia: ~1.3x o bitrate da camada alta. Vale sempre a pena
 * a partir de 3 participantes. Ver docs/05.
 *
 * A ordem é do mais pequeno para o maior, e é a ordem em que o mediasoup
 * numera as camadas: `spatialLayer: 0` é `scaleResolutionDownBy: 4`.
 */
export const SIMULCAST_ENCODINGS = [
  { rid: 'low', maxBitrate: 150_000, scaleResolutionDownBy: 4, scalabilityMode: 'L1T3' },
  { rid: 'mid', maxBitrate: 500_000, scaleResolutionDownBy: 2, scalabilityMode: 'L1T3' },
  { rid: 'high', maxBitrate: 1_500_000, scaleResolutionDownBy: 1, scalabilityMode: 'L1T3' },
] as const;

/**
 * Prioridade por fonte, para quando a banda não dá para tudo.
 *
 * O ecrã acima da câmara porque é onde está a informação: um screenshare de
 * código ilegível é pior que uma cara granulada. O áudio nunca entra nesta
 * conta — não é simulcast, não se degrada, e cortá-lo seria cortar a
 * conversa.
 */
export const CONSUMER_PRIORITY = {
  screen: 255,
  cam: 128,
  mic: 255,
} as const;

/**
 * Escolhe a camada espacial a partir da largura a que o vídeo está a ser
 * mostrado, em pixels CSS já multiplicados pelo devicePixelRatio.
 *
 * Os limiares são as larguras das próprias camadas com uma margem: pedir a
 * camada cuja resolução é imediatamente superior ao tamanho de render evita
 * tanto o desperdício como a imagem esticada. A histerese é do chamador —
 * ver `apps/web/src/voice/VideoGrid.tsx`, onde um redimensionamento a
 * arrastar o rato não deve gerar cem pedidos.
 */
export function spatialLayerForWidth(renderedWidth: number): 0 | 1 | 2 {
  if (renderedWidth <= 320) return 0;
  if (renderedWidth <= 640) return 1;
  return 2;
}

export const VIDEO_PROFILES = {
  low: { width: 320, height: 180, frameRate: 15, bitrate: 150_000 },
  medium: { width: 640, height: 360, frameRate: 24, bitrate: 500_000 },
  high: { width: 1280, height: 720, frameRate: 30, bitrate: 1_500_000 },
} as const;

export const SCREEN_PROFILES = {
  /**
   * Texto e código: resolução alta, poucos frames.
   *
   * `contentHint: 'detail'` diz ao codificador para preservar nitidez em vez
   * de suavizar movimento — é a diferença entre conseguir e não conseguir
   * ler uma linha de código do outro lado.
   */
  text: {
    width: 1920,
    height: 1080,
    frameRate: 5,
    bitrate: 800_000,
    contentHint: 'detail',
  },
  /** Vídeo e jogos: resolução média, muitos frames. */
  motion: {
    width: 1280,
    height: 720,
    frameRate: 30,
    bitrate: 2_500_000,
    contentHint: 'motion',
  },
} as const;

export type ScreenProfile = keyof typeof SCREEN_PROFILES;
export type VideoProfile = keyof typeof VIDEO_PROFILES;

export interface VoiceStateData {
  userId: string;
  channelId: string | null;
  guildId: string | null;
  selfMute: boolean;
  selfDeaf: boolean;
  selfVideo: boolean;
  selfStream: boolean;
  /** Imposto por moderação, não pelo próprio. */
  serverMute: boolean;
  serverDeaf: boolean;
}

export interface IceServerConfig {
  urls: string[];
  username?: string;
  credential?: string;
}
